// v1.0.0 - 2026-02-02 - KPI Card component for Dashboard

import { useMemo } from 'react'
import { Card } from '@/components/ui'
import { cn } from '@/lib/utils/cn'

interface KPICardProps {
  title: string
  value: number
  unit?: string
  trend?: number | null
  trendLabel?: string
  variant?: 'default' | 'success' | 'warning' | 'info'
  formatValue?: (value: number) => string
  icon?: React.ReactNode
}

// Singleton formatters for performance
const numberFormatter = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
})

export function formatKwhValue(value: number): string {
  return `${numberFormatter.format(value)} kWh`
}

export function formatCurrencyValue(value: number): string {
  return currencyFormatter.format(value)
}

const variantStyles = {
  default: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'text-gray-600',
    value: 'text-gray-900',
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    value: 'text-green-900',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    value: 'text-amber-900',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    value: 'text-blue-900',
  },
}

export function KPICard({
  title,
  value,
  unit,
  trend,
  trendLabel = 'vs mese prec.',
  variant = 'default',
  formatValue,
  icon,
}: KPICardProps) {
  const styles = variantStyles[variant]

  const formattedValue = useMemo(() => {
    if (formatValue) return formatValue(value)
    if (unit === 'EUR') return currencyFormatter.format(value)
    if (unit === 'kWh') return `${numberFormatter.format(value)} kWh`
    return numberFormatter.format(value)
  }, [value, unit, formatValue])

  const trendDisplay = useMemo(() => {
    if (trend === null || trend === undefined) return null
    const isPositive = trend >= 0
    const trendText = `${isPositive ? '+' : ''}${trend}%`
    return { isPositive, trendText }
  }, [trend])

  return (
    <Card className={cn('relative overflow-hidden', styles.bg, 'border', styles.border)}>
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--color-text-secondary)]">{title}</p>
            <p className={cn('text-2xl font-bold', styles.value)}>{formattedValue}</p>
          </div>
          {icon && (
            <div className={cn('rounded-lg p-2', styles.bg, styles.icon)}>
              {icon}
            </div>
          )}
        </div>

        {trendDisplay && (
          <div className="mt-3 flex items-center gap-1.5">
            <span
              className={cn(
                'inline-flex items-center text-sm font-medium',
                trendDisplay.isPositive ? 'text-green-600' : 'text-red-600'
              )}
            >
              {trendDisplay.isPositive ? (
                <svg className="h-4 w-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              ) : (
                <svg className="h-4 w-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              )}
              {trendDisplay.trendText}
            </span>
            <span className="text-xs text-[var(--color-text-secondary)]">{trendLabel}</span>
          </div>
        )}
      </div>
    </Card>
  )
}

// Preset icons for common KPIs
export const KPIIcons = {
  energy: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  download: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  upload: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  ),
  currency: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  shared: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  ),
}
